import { ImageIcon } from '@music-vine/cadence/icons';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@music-vine/cadence/ui';
import { motion } from 'motion/react';
import Image from 'next/image';
import Link from 'next/link';
import { memo } from 'react';
import { carouselEdgeArrowClassName } from '@/components/TileCarousel';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { cn } from '@/lib/utils';
import type { SearchResponseHit, VideoCategory } from '@music-vine/setlist';
import { getZoneThumbnailUrl } from '@/features/StockFootage/utils/get-zone-thumbnail-url';
interface SVLCategoryCarouselProps {
  closePopup: () => void;
  data: SearchResponseHit<VideoCategory>[] | undefined;
  onTileClick?: (slug: string) => void;
}
export const SVLCategoryCarousel = memo(({
  data,
  closePopup,
  onTileClick
}: SVLCategoryCarouselProps) => {
  const {
    isMd
  } = useTWScreens();
  if (!data?.length) {
    return null;
  }
  const AnimatedContainer = isMd ? motion.div : 'div';
  return <AnimatedContainer animate={isMd ? {
    opacity: 1
  } : undefined} aria-label="Stock video categories carousel" className={cn('group/carousel relative -mx-4 overflow-hidden md:-mx-6', 'max-md:[&_*]:animation-none! max-md:[&_*]:transition-none!')} data-testid="stock-video-category-carousel" initial={isMd ? {
    opacity: 0
  } : undefined} transition={isMd ? {
    duration: 0.2
  } : undefined}>
        <Carousel opts={{
      align: 'start',
      dragFree: !isMd
    }}>
          {/* containerClassName applies pl to the Embla viewport so snapping accounts for it correctly */}
          <CarouselContent className="mr-3 ml-0" containerClassName="pl-4 md:pl-6">
            {data.map(({
          document
        }, index) => {
          if (!(document.slug && (document.breadcrumb_name || document.name))) {
            return null;
          }
          const href = `/stock-video/category/${document.slug}`;
          const imageUrl = document.thumbnail_url ? getZoneThumbnailUrl(document.thumbnail_url) : undefined;
          return <CarouselItem className="basis-[48%] pr-2 pl-0" key={`svl-category-${document.id}-${index}`}>
                  <Link className="-m-1 flex flex-col rounded-lg p-2 transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-pink-500" href={href} onClick={() => {
              closePopup();
              onTileClick?.(href);
            }}>
                    <div className="relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-lg bg-gray-100 transition group-focus-visible:ring-2 group-focus-visible:ring-pink-500 dark:bg-gray-800">
                      {imageUrl ? <Image alt={document.breadcrumb_name || document.name} className="object-cover" fill sizes="(max-width: 768px) 48vw, 48vw" src={imageUrl} /> : <ImageIcon aria-hidden="true" className="size-8 text-gray-300 dark:text-gray-600" />}
                    </div>
                    <div className="mt-0.5">
                      <span className="font-semibold text-gray-900 text-sm dark:text-gray-100">
                        {document.breadcrumb_name || document.name}
                      </span>
                    </div>
                  </Link>
                </CarouselItem>;
        })}
          </CarouselContent>
          <CarouselPrevious className={cn('left-0 z-[1] hidden h-full w-container rounded-none bg-white/70 opacity-0 transition-all duration-300 enabled:hover:bg-white/70 disabled:opacity-0 enabled:group-hover/carousel:opacity-100 sm:flex dark:bg-gray-900/80 dark:enabled:hover:bg-gray-900/80', carouselEdgeArrowClassName)} tabIndex={-1} variant="transparent" />
          <CarouselNext className={cn('right-0 z-[1] hidden h-full w-container rounded-none bg-white/70 opacity-0 transition-all duration-300 enabled:hover:bg-white/70 disabled:opacity-0 enabled:group-hover/carousel:opacity-100 sm:flex dark:bg-gray-900/80 dark:enabled:hover:bg-gray-900/80', carouselEdgeArrowClassName)} tabIndex={-1} variant="transparent" />
        </Carousel>
      </AnimatedContainer>;
});
SVLCategoryCarousel.displayName = 'SVLCategoryCarousel';